/**
 * The sign-in pages, ours rather than Cognito's.
 *
 * JP's call (IDENTITY.md, 2026-08-10): no hosted UI. The browser never leaves
 * app.easting.ai, so the first screen a paying customer sees is the product's
 * rather than an AWS-shaped detour. What that costs is this file: sign-in,
 * invite redemption, forgot-password, and the MFA prompt, all of them states
 * of one small machine.
 *
 * Sign-up lives in `Signup.tsx` from 2026-08-15 and is a third door beside
 * checkout and the operator's invite. The pool still has self-signup switched
 * off: `POST /v1/signup` creates the person server-side through the same
 * `AdminCreateUser` the claim flow uses, so the login surface never widened
 * and the stack test asserting no user pool domain stays green.
 */

import { useState } from "preact/hooks";
import type { Auth, AuthState, PastedKeyAuth } from "../auth";

type Screen = "password" | "new-password" | "totp" | "forgot" | "reset";

export interface SignInProps {
  auth: Auth;
  devAuth: PastedKeyAuth;
  onSignedIn: (state: AuthState) => void;
}

export function SignIn({ auth, devAuth, onSignedIn }: SignInProps) {
  const [screen, setScreen] = useState<Screen>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function guard(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "That did not work.",
      );
    } finally {
      setBusy(false);
    }
  }

  const submit = (event: Event) => {
    event.preventDefault();
    if (screen === "password") {
      return guard(async () => {
        const state = await auth.signIn(email, password);
        if (state.kind === "new-password") setScreen("new-password");
        else if (state.kind === "totp") setScreen("totp");
        else onSignedIn(state);
      });
    }
    if (screen === "new-password") {
      return guard(async () =>
        onSignedIn(await auth.completeNewPassword(password)),
      );
    }
    if (screen === "totp") {
      return guard(async () => onSignedIn(await auth.submitTotp(code)));
    }
    if (screen === "forgot") {
      return guard(async () => {
        await auth.forgotPassword(email);
        setNote(`If ${email} has an account, a reset code is on its way.`);
        setScreen("reset");
      });
    }
    return guard(async () => {
      await auth.confirmForgotPassword(email, code, password);
      setNote("Password changed. Sign in with it.");
      setScreen("password");
    });
  };

  return (
    <div class="signin">
      <div class="signin-column">
        <form class="signin-card" onSubmit={submit}>
          <a class="wordmark" href="https://easting.ai">
            easting<span>.ai</span>
          </a>
          <h1>{HEADINGS[screen]}</h1>
          <p class="muted small">{BLURBS[screen]}</p>

          {(screen === "password" ||
            screen === "forgot" ||
            screen === "reset") && (
            <label>
              Email
              <input
                type="email"
                autocomplete="username"
                required
                value={email}
                onInput={(event) =>
                  setEmail((event.target as HTMLInputElement).value)
                }
              />
            </label>
          )}

          {(screen === "totp" || screen === "reset") && (
            <label>
              {screen === "totp" ? "Authenticator code" : "Reset code"}
              <input
                inputMode="numeric"
                autocomplete="one-time-code"
                required
                value={code}
                onInput={(event) =>
                  setCode((event.target as HTMLInputElement).value)
                }
              />
            </label>
          )}

          {screen !== "totp" && screen !== "forgot" && (
            <label>
              {screen === "password" ? "Password" : "New password"}
              <input
                type="password"
                autocomplete={
                  screen === "password" ? "current-password" : "new-password"
                }
                required
                minLength={screen === "password" ? undefined : 12}
                value={password}
                onInput={(event) =>
                  setPassword((event.target as HTMLInputElement).value)
                }
              />
            </label>
          )}

          {note && <p class="note">{note}</p>}
          {error && <p class="error-text">{error}</p>}

          <button
            type="submit"
            class="primary wide"
            disabled={busy || !auth.configured}
          >
            {busy ? "Working…" : ACTIONS[screen]}
          </button>

          {screen === "password" && (
            <button
              type="button"
              class="link"
              onClick={() => setScreen("forgot")}
            >
              Forgot your password?
            </button>
          )}
          {screen !== "password" && (
            <button
              type="button"
              class="link"
              onClick={() => setScreen("password")}
            >
              Back to sign in
            </button>
          )}

          <p class="muted small">
            No account yet? <a href="/signup">Start free</a>: three documents,
            no card. Or{" "}
            <a href="https://easting.ai/products/deeds/">see the plans</a>.
          </p>

          {!auth.configured && (
            <DevSignIn devAuth={devAuth} onSignedIn={onSignedIn} />
          )}
        </form>
        {/* The site's footer links, so the first screen a customer sees
            carries the same frame as the page they arrived from. */}
        <p class="signin-footer">
          <a href="https://docs.easting.ai/web/">Help</a>
          <a href="https://easting.ai/terms/">Terms</a>
          <a href="https://easting.ai/privacy/">Privacy</a>
          <a href="mailto:support@easting.ai">support@easting.ai</a>
        </p>
      </div>
    </div>
  );
}

/**
 * The local-development door, and only that.
 *
 * `task portal:dev` runs against `task api:up`, which has no Cognito, so
 * there is no password to type. It renders when the build has no pool
 * compiled in, which is never true of a deployed build.
 */
function DevSignIn({
  devAuth,
  onSignedIn,
}: {
  devAuth: PastedKeyAuth;
  onSignedIn: (state: AuthState) => void;
}) {
  const [key, setKey] = useState("");
  return (
    <div class="dev-signin">
      <p class="muted small">
        This build has no user pool configured, which means it is a local one.
        Paste an
        <code> east_live_ </code> key to talk to the API directly.
      </p>
      <input
        placeholder="east_live_…"
        value={key}
        onInput={(event) => setKey((event.target as HTMLInputElement).value)}
      />
      <button
        type="button"
        onClick={async () => {
          await devAuth.signInWithKey(key);
          onSignedIn({ kind: "signed-in", email: "local development" });
        }}
      >
        Use this key
      </button>
    </div>
  );
}

const HEADINGS: Record<Screen, string> = {
  password: "Sign in",
  "new-password": "Choose your password",
  totp: "Two-factor code",
  forgot: "Reset your password",
  reset: "Enter your reset code",
};

const BLURBS: Record<Screen, string> = {
  password: "Your usage, your keys, and your documents.",
  "new-password":
    "Your invite's temporary password is spent. Pick one of your own, at least 12 characters.",
  totp: "Open your authenticator app and enter the current code.",
  forgot: "We will email a code to the address on the account.",
  reset: "The code from your email, and the password you want.",
};

const ACTIONS: Record<Screen, string> = {
  password: "Sign in",
  "new-password": "Set password and continue",
  totp: "Verify",
  forgot: "Send the code",
  reset: "Change password",
};
